import {useEffect, useState, useCallback, useMemo} from 'react';
import {api} from '../lib/auth.js';
import {kr} from '../lib/format.js';

/**
 * Tavlor — kanban boards, for the sales pipeline and for plain work.
 *
 * A card can carry MRR and one-off revenue lines and can point at a customer in the
 * register, so a pipeline board reads as money per column. Those figures are SALES
 * ESTIMATES: they live only in `board_cards`, are never sent to Fortnox and are never
 * folded into anything the dashboard computes. The column totals say so.
 *
 * Moves are drag-and-drop. The client reorders optimistically and the server renumbers
 * the target column, so two quick drags cannot leave two cards with the same position.
 */

const STORE = 'jabr.board';
const remember = id => { try { localStorage.setItem(STORE, id ?? ''); } catch { /* private mode */ } };
const recall = () => { try { return localStorage.getItem(STORE) || null; } catch { return null; } };

async function call(path, opts) {
	const res = await api(path, opts && {...opts, body: opts.body && JSON.stringify(opts.body)});
	const json = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error(json.error ?? `API svarade ${res.status}`);
	return json;
}

const sum = (cards, type) => cards.reduce((a, c) =>
	a + (c.revenue ?? []).filter(r => r.type === type).reduce((x, r) => x + r.amount, 0), 0);

const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti',
	'september', 'oktober', 'november', 'december'];
const monthName = m => (m ? `${MONTHS[+m.slice(5, 7) - 1]} ${m.slice(0, 4)}` : '');

/** Invoiced MRR over cards, each customer once — the same rule the API's list uses. */
const invoiced = (cards, figures) => {
	const out = {abonnemang: 0, rorliga: 0, moduler: 0, total: 0};
	for (const id of new Set(cards.map(c => c.customer).filter(Boolean))) {
		const f = figures?.[id];
		if (f) for (const k in out) out[k] += f[k];
	}
	return out;
};

/** The five invoiced lines, in the order and wording of the chart of accounts. */
function Figures({f, compact}) {
	const rows = [
		['Arvoden abonnemang', f.abonnemang], ['Arvoden rörliga', f.rorliga],
		['Arvoden moduler', f.moduler],
	];
	return (
		<dl className={`kb-fig${compact ? ' kb-fig--compact' : ''}`}>
			{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{kr(v)}</dd></div>)}
			<div className="kb-fig__sum"><dt>Totalt MRR</dt><dd>{kr(f.total)}</dd></div>
			<div><dt>ARR</dt><dd>{kr(f.total * 12)}</dd></div>
		</dl>
	);
}

/**
 * How cards are ordered inside each column. `manual` is the dragged order the server keeps;
 * every other key sorts on read and leaves that order untouched, so switching back to
 * Manuell restores exactly what was arranged by hand.
 */
const SORTS = {
	manual: {label: 'Manuell ordning'},
	name: {label: 'Namn', text: true, value: c => c.title},
	number: {label: 'Kundnummer', text: true, value: c => c.customer ?? ''},
	total: {label: 'Totalt MRR', value: (c, f) => f?.total},
	arr: {label: 'ARR', value: (c, f) => (f ? f.total * 12 : null)},
	abonnemang: {label: 'Arvoden abonnemang', value: (c, f) => f?.abonnemang},
	rorliga: {label: 'Arvoden rörliga', value: (c, f) => f?.rorliga},
	moduler: {label: 'Arvoden moduler', value: (c, f) => f?.moduler},
	pipeline: {label: 'Pipeline MRR', value: c => sum([c], 'mrr') || null},
	contact: {label: 'Senaste kontakt', text: true, value: c => c.details?.last_contact},
};
const sortKey = id => `jabr.board.sort.${id}`;
function readSort(id) {
	try {
		const v = JSON.parse(localStorage.getItem(sortKey(id)) ?? 'null');
		if (v && SORTS[v.key] && (v.dir === 1 || v.dir === -1)) return v;
	} catch { /* private mode or garbage */ }
	return {key: 'manual', dir: 1};
}

/**
 * Cards without a value (no customer, or no invoice that month) always sort LAST in either
 * direction — at the top of a descending MRR column they would read as the biggest.
 * Customer numbers compare numerically, so 10000090 does not land between 1000001 and 2.
 */
function compare(sort, figures) {
	const s = SORTS[sort.key];
	if (!s.value) return (a, b) => a.order - b.order;
	const coll = new Intl.Collator('sv', {numeric: true, sensitivity: 'base'});
	return (a, b) => {
		const x = s.value(a, figures?.[a.customer]);
		const y = s.value(b, figures?.[b.customer]);
		const nx = x == null || x === '', ny = y == null || y === '';
		if (nx || ny) return nx && ny ? coll.compare(a.title, b.title) : nx ? 1 : -1;
		const d = s.text ? coll.compare(String(x), String(y)) : x - y;
		return d * sort.dir || coll.compare(a.title, b.title);
	};
}

const when = d => new Date(d).toLocaleString('sv-SE', {dateStyle: 'short', timeStyle: 'short'});

export default function Boards() {
	const [id, setId] = useState(recall);
	const open = v => { remember(v); setId(v); };
	return id ? <Board id={id} onBack={() => open(null)} /> : <Overview onOpen={open} />;
}

function Overview({onOpen}) {
	const [list, setList] = useState(null);
	const [error, setError] = useState(null);
	const [name, setName] = useState('');
	const [columns, setColumns] = useState('');

	const load = useCallback(() => call('/api/boards').then(setList).catch(e => setError(e.message)), []);
	useEffect(() => { load(); }, [load]);

	const createCustomers = async () => {
		try {
			const r = await call('/api/boards/customers', {method: 'POST'});
			onOpen(r._id);
		} catch (e) { setError(e.message); }
	};
	const hasCustomers = (list ?? []).some(b => b.kind === 'customers');

	const create = async () => {
		try {
			const b = await call('/api/boards', {method: 'POST', body: {
				name, columns: columns.split(',').map(s => s.trim()).filter(Boolean),
			}});
			onOpen(b._id);
		} catch (e) { setError(e.message); }
	};

	if (!list && !error) return <div className="state">Hämtar…</div>;
	return (
		<>
			<p className="sub">
				Kanbantavlor för säljpipeline och arbete. MRR och engångsbelopp på korten är
				säljuppskattningar. De ligger bara i Jabr och räknas aldrig in i bokförda siffror.
			</p>
			{error && <div className="panel panel--note"><p>{error}</p></div>}
			<div className="cards">
				{(list ?? []).map(b => (
					<button key={b._id} className="card kb-boardcard" onClick={() => onOpen(b._id)}>
						<span className="card__label">{b.columns.length} kolumner · {b.card_count} kort</span>
						<span className="card__value">{b.name}</span>
						{b.invoiced.mrr > 0 && (
							<span className="card__hint">
								Fakturerat {monthName(b.month)}: MRR {kr(b.invoiced.mrr)} · ARR {kr(b.invoiced.arr)}
							</span>
						)}
						{(b.totals.mrr > 0 || b.totals.one_off > 0) && (
							<span className="card__hint">
								Pipeline: MRR {kr(b.totals.mrr)} kr/mån · engång {kr(b.totals.one_off)} kr
							</span>
						)}
					</button>
				))}
			</div>
			{list && !hasCustomers && (
				<div className="panel">
					<div className="panel__title">Kundtavla</div>
					<p>
						Ett kort per aktiv kund med fakturerade arvoden per månad (abonnemang, rörliga,
						moduler), totalt MRR och ARR, ur Fortnox. Synka-knappen på tavlan hämtar nya
						kunder och fakturor.
					</p>
					<button className="btn btn--primary" style={{marginTop: 10}} onClick={createCustomers}>
						Skapa kundtavla
					</button>
				</div>
			)}
			<div className="panel">
				<div className="panel__title">Ny tavla</div>
				<div className="fieldrow">
					<label className="field">
						<span>Namn</span>
						<input className="search" value={name} placeholder="Säljpipeline"
							onChange={e => setName(e.target.value)} />
					</label>
					<label className="field field--grow">
						<span>Kolumner, kommaseparerade (tomt = Att göra, Pågår, Klart)</span>
						<input className="search" value={columns}
							placeholder="Lead, Demo, Offert, Vunnen, Förlorad"
							onChange={e => setColumns(e.target.value)} />
					</label>
					<button className="btn btn--primary" disabled={!name.trim()} onClick={create}>Skapa tavla</button>
				</div>
			</div>
		</>
	);
}

function Board({id, onBack}) {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [drag, setDrag] = useState(null);          // card id being dragged
	// {column, before}: drop into `column` ahead of card `before`, or at the end when null.
	// Keyed by card rather than index so a drop lands right while a filter hides cards.
	const [target, setTarget] = useState(null);
	const [editing, setEditing] = useState(null);    // card id, or {column} for a new card
	const [panel, setPanel] = useState(null);        // 'settings' | 'stats'
	const [q, setQ] = useState('');
	const [who, setWho] = useState('');
	const [sort, setSortState] = useState(() => readSort(id));
	const setSort = v => {
		setSortState(v);
		try { localStorage.setItem(sortKey(id), JSON.stringify(v)); } catch { /* private mode */ }
	};
	const [syncing, setSyncing] = useState(false);
	const [synced, setSynced] = useState(null);

	const load = useCallback(() => call(`/api/boards/${id}`)
		.then(d => { setData(d); setError(null); })
		.catch(e => setError(e.message)), [id]);
	useEffect(() => { load(); }, [load]);

	const names = useMemo(() => ({
		user: Object.fromEntries((data?.users ?? []).map(u => [u._id, u.name || u.email])),
		customer: Object.fromEntries((data?.customers ?? []).map(c => [c._id, c.Name])),
	}), [data]);

	if (error && !data) {
		return (
			<div className="state">
				Kunde inte hämta tavlan: {error}{' '}
				<button className="btn btn--sm" onClick={onBack}>Alla tavlor</button>
			</div>
		);
	}
	if (!data) return <div className="state">Hämtar…</div>;

	const {board, cards, figures, month} = data;
	const linked = cards.some(c => c.customer);

	const sync = async () => {
		setSyncing(true);
		try {
			setSynced(await call(`/api/boards/${board._id}/sync`, {method: 'POST'}));
			await load();
		} catch (e) { setError(e.message); } finally { setSyncing(false); }
	};
	const needle = q.trim().toLowerCase();
	const owners = [...new Set(cards.flatMap(c => c.details?.owners ?? []))].sort();
	const visible = cards.filter(c => (!who
		|| (who.startsWith('owner:') ? (c.details?.owners ?? []).includes(who.slice(6)) : c.assignee === who))
		&& (!needle || [c.title, names.customer[c.customer], c.details?.contact_name,
			c.details?.next_step, c.details?.erp].some(t => (t ?? '').toLowerCase().includes(needle))));
	const inColumn = col => visible.filter(c => c.column === col).sort(compare(sort, figures));
	const orphans = cards.filter(c => !board.columns.some(col => col._id === c.column));

	const drop = async () => {
		if (!drag || !target) return;
		const card = cards.find(c => c._id === drag);
		setDrag(null); setTarget(null);
		if (target.before === drag) return;
		// The server's index counts the target column WITHOUT the moved card.
		const list = cards.filter(c => c.column === target.column).sort((a, b) => a.order - b.order);
		const rest = list.filter(c => c._id !== drag);
		const at = rest.findIndex(c => c._id === target.before);
		const index = at === -1 ? rest.length : at;
		if (card.column === target.column && list.findIndex(c => c._id === drag) === index) return;
		rest.splice(index, 0, {...card, column: target.column});
		const order = Object.fromEntries(rest.map((c, i) => [c._id, i]));
		setData(d => ({...d, cards: d.cards.map(c => (c._id in order
			? {...c, column: target.column, order: order[c._id]} : c))}));
		try {
			await call(`/api/cards/${drag}/move`, {method: 'POST', body: {column: target.column, index}});
		} catch (e) { setError(e.message); }
		load();
	};

	return (
		<>
			<div className="toolbar">
				<button className="btn btn--sm" onClick={onBack}>← Alla tavlor</button>
				<span className="kb-title">{board.name}</span>
				<span className="stamp">
					{cards.length} kort
					{linked && ` · fakturerat ${monthName(month)}: MRR ${kr(invoiced(cards, figures).total)} · ARR ${kr(invoiced(cards, figures).total * 12)}`}
					{(sum(cards, 'mrr') > 0 || sum(cards, 'one_off') > 0)
						&& ` · pipeline: MRR ${kr(sum(cards, 'mrr'))} · engång ${kr(sum(cards, 'one_off'))}`}
				</span>
				<span className="spacer" />
				<input className="search kb-search" placeholder="Sök kort, kund, kontakt" value={q}
					onChange={e => setQ(e.target.value)} />
				<span className="kb-sort">
					<select className="select" value={sort.key} aria-label="Sortera korten"
						onChange={e => setSort({key: e.target.value,
							dir: (SORTS[e.target.value].text && e.target.value !== 'contact') || e.target.value === 'manual' ? 1 : -1})}>
						{Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>Sortera: {v.label}</option>)}
					</select>
					{sort.key !== 'manual' && (
						<button className="btn btn--sm" onClick={() => setSort({...sort, dir: -sort.dir})}
							title={sort.dir === 1 ? 'Stigande' : 'Fallande'}>
							{sort.dir === 1 ? '↑ stigande' : '↓ fallande'}
						</button>
					)}
				</span>
				<select className="select" value={who} onChange={e => setWho(e.target.value)}>
					<option value="">Alla ansvariga</option>
					{data.users.map(u => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
					{owners.map(o => <option key={o} value={`owner:${o}`}>{o} (GitHub)</option>)}
				</select>
				{(board.kind === 'customers' || linked) && (
					<button className="btn" onClick={sync} disabled={syncing}>
						{syncing ? 'Synkar…' : 'Synka från Fortnox'}
					</button>
				)}
				<button className="btn" onClick={() => setPanel('stats')}>Statistik</button>
				<button className="btn" onClick={() => setPanel('settings')}>Inställningar</button>
			</div>
			{error && <div className="panel panel--note"><p>{error}</p></div>}
			{synced && <SyncResult r={synced} onClose={() => setSynced(null)} />}
			{orphans.length > 0 && (
				<div className="panel panel--note"><p>{orphans.length} kort pekar på en kolumn som inte finns.</p></div>
			)}

			<div className="kb">
				{board.columns.map(col => {
					const list = inColumn(col._id);
					const all = cards.filter(c => c.column === col._id);
					const here = target?.column === col._id;
					return (
						<section key={col._id} className={`kb-col${here ? ' kb-col--over' : ''}`}
							onDragOver={e => { e.preventDefault(); if (!here || target.before) setTarget({column: col._id, before: null}); }}
							onDrop={e => { e.preventDefault(); drop(); }}>
							<header className="kb-col__head">
								<span className="kb-col__name">{col.name}</span>
								<span className="kb-col__count">{all.length}</span>
							</header>
							{invoiced(all, figures).total > 0 && (
								<div className="kb-col__sum">
									MRR {kr(invoiced(all, figures).total)} · ARR {kr(invoiced(all, figures).total * 12)}
								</div>
							)}
							{(sum(all, 'mrr') > 0 || sum(all, 'one_off') > 0) && (
								<div className="kb-col__sum">
									Pipeline: MRR {kr(sum(all, 'mrr'))} · engång {kr(sum(all, 'one_off'))}
								</div>
							)}
							<div className="kb-col__body">
								{list.map((c, i) => (
									<article key={c._id} draggable
										className={`kb-card${drag === c._id ? ' kb-card--drag' : ''}${here && target.before === c._id ? ' kb-card--before' : ''}`}
										onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDrag(c._id); }}
										onDragEnd={() => { setDrag(null); setTarget(null); }}
										onDragOver={e => {
											e.preventDefault(); e.stopPropagation();
											const r = e.currentTarget.getBoundingClientRect();
											// Sorted, a position inside the column means nothing: the drop only
											// changes the column, and the manual order is kept for when it returns.
											const before = sort.key !== 'manual' ? null
												: e.clientY < r.top + r.height / 2 ? c._id : (list[i + 1]?._id ?? null);
											if (!here || target.before !== before) setTarget({column: col._id, before});
										}}
										onClick={() => setEditing(c._id)}>
										<div className="kb-card__title">{c.title}</div>
										{c.customer && names.customer[c.customer] !== c.title && (
											<div className="kb-card__meta">{names.customer[c.customer] ?? `Kund ${c.customer}`}</div>
										)}
										{c.customer && figures[c.customer] && <Figures f={figures[c.customer]} compact />}
										{c.details?.next_step && <div className="kb-card__meta">Nästa steg: {c.details.next_step}</div>}
										{(c.details?.last_contact || c.details?.last_event) && (
											<div className="kb-card__meta">
												{[c.details.last_contact, c.details.last_event].filter(Boolean).join(' · ')}
											</div>
										)}
										<div className="kb-card__foot">
											{sum([c], 'mrr') > 0 && <span className="pill kb-pill">pipeline MRR {kr(sum([c], 'mrr'))}</span>}
											{sum([c], 'one_off') > 0 && <span className="pill kb-pill kb-pill--open">engång {kr(sum([c], 'one_off'))}</span>}
											{c.comments?.length > 0 && <span className="kb-card__meta">{c.comments.length} kommentarer</span>}
											<span className="spacer" />
											{(c.assignee || c.details?.owners?.length) && (
												<span className="kb-card__meta">
													{[c.assignee && (names.user[c.assignee] ?? '?'), ...(c.details?.owners ?? [])].filter(Boolean).join(', ')}
												</span>
											)}
										</div>
									</article>
								))}
								{here && target.before === null && drag && sort.key === 'manual' && <div className="kb-drop" />}
							</div>
							<button className="btn btn--sm kb-add" onClick={() => setEditing({column: col._id})}>+ Nytt kort</button>
						</section>
					);
				})}
			</div>
			<p className="foot-note">
				Belopp i kronor exklusive moms.
				{linked && <> Fakturerade arvoden gäller {monthName(month)}, senaste hela månaden, ur
				Fortnox fakturaregister: abonnemang (3010), rörliga (3011) och moduler (3012) per
				kund, förutbetalda perioder utslagna per månad och aldrig under noll. Totalt MRR är
				summan av de tre och ARR tolv gånger den. Det är bredare än dashboardens MRR, som
				bara räknar abonnemang.</>}
				{' '}Pipeline-belopp är säljets uppskattningar och räknas aldrig in i fakturerat.
				Flytt mellan kolumner loggas på kortet och är underlaget för statistiken.
			</p>

			{editing && (
				<CardDrawer key={typeof editing === 'string' ? editing : 'new'} data={data} names={names}
					card={typeof editing === 'string' ? cards.find(c => c._id === editing) : null}
					column={typeof editing === 'string' ? null : editing.column}
					figures={figures} month={month}
					onClose={() => setEditing(null)} onSaved={load} />
			)}
			{panel === 'settings' && (
				<Settings board={board} cards={cards} onClose={() => setPanel(null)} onSaved={load}
					onDeleted={() => { setPanel(null); onBack(); }} />
			)}
			{panel === 'stats' && <Stats board={board} onClose={() => setPanel(null)} />}
		</>
	);
}

function Drawer({title, sub, onClose, children}) {
	useEffect(() => {
		const onKey = e => e.key === 'Escape' && onClose();
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose]);
	return (
		<div className="drawer" onMouseDown={e => e.target === e.currentTarget && onClose()}>
			<div className="drawer__panel">
				<div className="drawer__head">
					<span className="drawer__title">{title}</span>
					<button className="btn btn--sm drawer__close" onClick={onClose}>Stäng</button>
				</div>
				{sub && <div className="drawer__sub">{sub}</div>}
				{children}
			</div>
		</div>
	);
}

/** The prospect fields, in the order the GitHub sales board showed them. */
const DETAIL_FIELDS = [
	['contact_name', 'Kontaktperson', ''],
	['contact_phone', 'Telefon', ''],
	['contact_email', 'E-post', ''],
	['next_step', 'Nästa steg', 'Boka demo'],
	['last_contact', 'Senaste kontakt', 'ÅÅÅÅ-MM-DD'],
	['last_event', 'Händelse', 'Fysiskt möte'],
	['erp', 'Affärssystem', 'Pyramid'],
];

const BLANK_LINE = () => ({amount: '', type: 'mrr', description: ''});

function SyncResult({r, onClose}) {
	const part = (label, x, ok) => (x?.ok
		? <li>{label}: {ok(x)}</li> : <li>{label}: misslyckades, {x?.error}</li>);
	return (
		<div className="panel panel--note">
			<div className="panel__title">Synkat från Fortnox</div>
			<ul className="assump">
				{part('Kunder', r.customers, x => `${x.result.done.length} uppdaterade eller nya`
					+ (x.result.failed.length ? `, ${x.result.failed.length} misslyckades` : ''))}
				{part('Fakturor', r.invoices, x => `${x.fetched} ändrade sedan ${new Date(x.since).toLocaleString('sv-SE')}`
					+ (x.failed ? `, ${x.failed} kunde inte hämtas och tas nästa gång` : ''))}
				<li>Nya kort: {r.added.length ? r.added.join(', ') : 'inga'}</li>
			</ul>
			<button className="btn btn--sm" style={{marginTop: 8}} onClick={onClose}>OK</button>
		</div>
	);
}

function CardDrawer({data, names, card, column, figures, month, onClose, onSaved}) {
	const {board} = data;
	const [form, setForm] = useState(() => ({
		title: card?.title ?? '', description: card?.description ?? '',
		assignee: card?.assignee ?? '', customer: card?.customer ?? '',
		column: card?.column ?? column,
		revenue: (card?.revenue ?? []).map(r => ({...r, amount: String(r.amount)})),
		details: {
			...Object.fromEntries(DETAIL_FIELDS.map(([k]) => [k, card?.details?.[k] ?? ''])),
			labels: (card?.details?.labels ?? []).join(', '), owners: (card?.details?.owners ?? []).join(', '),
		},
	}));
	const setDetail = (k, v) => setForm(f => ({...f, details: {...f.details, [k]: v}}));
	const [comment, setComment] = useState('');
	const [error, setError] = useState(null);
	const [busy, setBusy] = useState(false);
	const set = (k, v) => setForm(f => ({...f, [k]: v}));
	const setLine = (i, k, v) => setForm(f => ({...f, revenue: f.revenue.map((r, j) => (j === i ? {...r, [k]: v} : r))}));
	const colName = idv => board.columns.find(c => c._id === idv)?.name;

	const run = async fn => {
		setBusy(true);
		try { await fn(); setError(null); } catch (e) { setError(e.message); } finally { setBusy(false); }
	};

	const save = () => run(async () => {
		const body = {
			title: form.title, description: form.description, details: form.details,
			assignee: form.assignee || null, customer: form.customer || null,
			revenue: form.revenue.filter(r => r.amount !== '').map(r => ({...r, amount: Number(String(r.amount).replace(/\s/g, '').replace(',', '.'))})),
		};
		if (card) {
			await call(`/api/cards/${card._id}`, {method: 'PUT', body});
			if (form.column !== card.column) {
				await call(`/api/cards/${card._id}/move`, {method: 'POST', body: {column: form.column, index: 1e6}});
			}
		} else {
			await call(`/api/boards/${board._id}/cards`, {method: 'POST', body: {...body, column: form.column}});
		}
		await onSaved();
		onClose();
	});

	const addComment = () => run(async () => {
		await call(`/api/cards/${card._id}/comments`, {method: 'POST', body: {text: comment}});
		setComment('');
		await onSaved();
	});

	const remove = () => run(async () => {
		if (!window.confirm(`Ta bort kortet "${card.title}"? Kommentarer och historik försvinner också.`)) return;
		await call(`/api/cards/${card._id}`, {method: 'DELETE'});
		await onSaved();
		onClose();
	});

	return (
		<Drawer title={card ? card.title : 'Nytt kort'} onClose={onClose}
			sub={card ? `Skapat ${when(card.created)} · ${colName(card.column) ?? ''}` : `I ${colName(column)}`}>
			{error && <div className="panel panel--note"><p>{error}</p></div>}
			{card?.customer && figures?.[card.customer] && (
				<div className="panel">
					<div className="panel__title">Fakturerat {monthName(month)}, ur Fortnox</div>
					<Figures f={figures[card.customer]} />
				</div>
			)}
			<div className="editgrid">
				<label className="editfield" style={{gridColumn: '1 / -1'}}>Rubrik
					<input className="search" value={form.title} onChange={e => set('title', e.target.value)} autoFocus={!card} />
				</label>
				<label className="editfield">Kolumn
					<select className="select" value={form.column} onChange={e => set('column', e.target.value)}>
						{board.columns.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
					</select>
				</label>
				<label className="editfield">Ansvarig
					<select className="select" value={form.assignee} onChange={e => set('assignee', e.target.value)}>
						<option value="">Ingen</option>
						{data.users.map(u => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
					</select>
				</label>
				<label className="editfield">Kund i registret
					<select className="select" value={form.customer} onChange={e => set('customer', e.target.value)}>
						<option value="">Ingen (prospekt)</option>
						{data.customers.map(c => <option key={c._id} value={c._id}>{c.Name} ({c._id})</option>)}
						{form.customer && !names.customer[form.customer] && <option value={form.customer}>Kund {form.customer}</option>}
					</select>
				</label>
				{DETAIL_FIELDS.map(([k, label, ph]) => (
					<label key={k} className="editfield">{label}
						<input className="search" value={form.details[k]} placeholder={ph}
							onChange={e => setDetail(k, e.target.value)} />
					</label>
				))}
				<label className="editfield">Ansvariga utanför Jabr
					<input className="search" value={form.details.owners} placeholder="kommaseparerat"
						onChange={e => setDetail('owners', e.target.value)} />
				</label>
				<label className="editfield">Etiketter
					<input className="search" value={form.details.labels} placeholder="Utland"
						onChange={e => setDetail('labels', e.target.value)} />
				</label>
				{card?.source?.url && (
					<div className="editfield" style={{gridColumn: '1 / -1'}}>
						Importerat från GitHub{card.source.state ? ` (${card.source.state.toLowerCase()} där)` : ''}:{' '}
						<a href={card.source.url} target="_blank" rel="noreferrer">{card.source.url}</a>
					</div>
				)}
				<label className="editfield" style={{gridColumn: '1 / -1'}}>Beskrivning
					<textarea className="search kb-text" rows={5} value={form.description}
						onChange={e => set('description', e.target.value)} />
				</label>
			</div>

			<div className="panel__title">Pipeline-intäkt (uppskattning)</div>
			<table style={{minWidth: 0, marginBottom: 8}}>
				<thead><tr><th>Typ</th><th>Belopp, kr</th><th>Beskrivning</th><th /></tr></thead>
				<tbody>
					{form.revenue.length === 0 && <tr><td colSpan={4}>Ingen intäkt angiven.</td></tr>}
					{form.revenue.map((r, i) => (
						<tr key={i}>
							<td>
								<select className="select select--sm" value={r.type} onChange={e => setLine(i, 'type', e.target.value)}>
									<option value="mrr">MRR per månad</option>
									<option value="one_off">Engång</option>
								</select>
							</td>
							<td><input className="search cell" inputMode="numeric" value={r.amount}
								onChange={e => setLine(i, 'amount', e.target.value)} /></td>
							<td><input className="search cell cell--wide" value={r.description}
								placeholder="5 licenser SALES" onChange={e => setLine(i, 'description', e.target.value)} /></td>
							<td><button className="btn btn--sm"
								onClick={() => setForm(f => ({...f, revenue: f.revenue.filter((_, j) => j !== i)}))}>Ta bort</button></td>
						</tr>
					))}
				</tbody>
			</table>
			<button className="btn btn--sm" onClick={() => setForm(f => ({...f, revenue: [...f.revenue, BLANK_LINE()]}))}>
				Lägg till intäktsrad
			</button>

			<div className="editbar">
				<button className="btn btn--primary" disabled={busy || !form.title.trim()} onClick={save}>
					{card ? 'Spara' : 'Skapa kort'}
				</button>
				<span className="spacer" />
				{card && <button className="btn" disabled={busy} onClick={remove}>Ta bort kort</button>}
			</div>

			{card && (
				<>
					<div className="panel__title" style={{marginTop: 18}}>Kommentarer</div>
					{card.comments.length === 0 && <p className="foot-note">Inga kommentarer ännu.</p>}
					{card.comments.map(c => (
						<div key={c._id} className="kb-comment">
							<div className="kb-card__meta">{c.email} · {when(c.at)}</div>
							<div className="kb-comment__text">{c.text}</div>
						</div>
					))}
					<div className="fieldrow" style={{marginTop: 8}}>
						<label className="field field--grow">
							<textarea className="search kb-text" rows={2} value={comment} placeholder="Skriv en kommentar"
								onChange={e => setComment(e.target.value)} />
						</label>
						<button className="btn" disabled={busy || !comment.trim()} onClick={addComment}>Kommentera</button>
					</div>

					<div className="panel__title" style={{marginTop: 18}}>Historik</div>
					<ul className="assump">
						{[...card.activity].reverse().map((a, i) => (
							<li key={i}>{when(a.at)} · {a.details} · {a.email}</li>
						))}
					</ul>
				</>
			)}
		</Drawer>
	);
}

function Settings({board, cards, onClose, onSaved, onDeleted}) {
	const [name, setName] = useState(board.name);
	const [cols, setCols] = useState(board.columns.map(c => ({...c})));
	const [error, setError] = useState(null);
	const count = idv => cards.filter(c => c.column === idv).length;
	const move = (i, d) => setCols(cs => {
		const next = [...cs];
		[next[i], next[i + d]] = [next[i + d], next[i]];
		return next;
	});

	const save = async () => {
		try {
			await call(`/api/boards/${board._id}`, {method: 'PUT', body: {name, columns: cols}});
			await onSaved();
			onClose();
		} catch (e) { setError(e.message); }
	};

	const remove = async () => {
		if (!window.confirm(`Radera tavlan "${board.name}" och alla ${cards.length} kort?`)) return;
		if (cards.length && !window.confirm('Bekräfta en gång till: kort, kommentarer och historik raderas permanent.')) return;
		try {
			await call(`/api/boards/${board._id}?confirm=1`, {method: 'DELETE'});
			remember(null);
			onDeleted();
		} catch (e) { setError(e.message); }
	};

	return (
		<Drawer title="Inställningar" sub={board.name} onClose={onClose}>
			{error && <div className="panel panel--note"><p>{error}</p></div>}
			<label className="field" style={{marginBottom: 16}}>
				<span>Namn</span>
				<input className="search" value={name} onChange={e => setName(e.target.value)} />
			</label>
			<div className="panel__title">Kolumner</div>
			<table style={{minWidth: 0, marginBottom: 8}}>
				<thead><tr><th>Namn</th><th>Kort</th><th /></tr></thead>
				<tbody>
					{cols.map((c, i) => (
						<tr key={c._id ?? `new${i}`}>
							<td><input className="search cell cell--wide" value={c.name}
								onChange={e => setCols(cs => cs.map((x, j) => (j === i ? {...x, name: e.target.value} : x)))} /></td>
							<td>{c._id ? count(c._id) : 0}</td>
							<td>
								<button className="btn btn--sm" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>{' '}
								<button className="btn btn--sm" disabled={i === cols.length - 1} onClick={() => move(i, 1)}>↓</button>{' '}
								<button className="btn btn--sm" disabled={c._id && count(c._id) > 0}
									title={c._id && count(c._id) > 0 ? 'Flytta korten först' : undefined}
									onClick={() => setCols(cs => cs.filter((_, j) => j !== i))}>Ta bort</button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
			<button className="btn btn--sm" onClick={() => setCols(cs => [...cs, {name: ''}])}>Lägg till kolumn</button>
			<p className="foot-note">
				En kolumn som har kort kan inte tas bort. Ett namnbyte behåller korten och historiken.
			</p>
			<div className="editbar">
				<button className="btn btn--primary" onClick={save}>Spara</button>
				<span className="spacer" />
				<button className="btn" onClick={remove}>Radera tavlan</button>
			</div>
		</Drawer>
	);
}

function Stats({board, onClose}) {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	useEffect(() => {
		call(`/api/boards/${board._id}/analytics`).then(setData).catch(e => setError(e.message));
	}, [board._id]);

	const csv = () => {
		const head = ['Vecka (söndag)', ...data.columns.map(c => c.name), 'Händelser'];
		const rows = data.weeks.map(w => [w.week, ...data.columns.map(c => w.counts[c._id] ?? 0), w.events]);
		const text = [head, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\r\n');
		const a = document.createElement('a');
		a.href = URL.createObjectURL(new Blob(['﻿', text], {type: 'text/csv;charset=utf-8'}));
		a.download = `${board.name} statistik.csv`;
		a.click();
		URL.revokeObjectURL(a.href);
	};

	return (
		<Drawer title="Statistik" onClose={onClose}
			sub="Antal kort i varje kolumn vid veckans slut, återskapat ur kortens historik. Raderade kort ingår inte.">
			{error && <div className="panel panel--note"><p>{error}</p></div>}
			{!data && !error && <div className="state">Hämtar…</div>}
			{data && (
				<>
					<div className="toolbar">
						<span className="stamp">{data.weeks.length} veckor</span>
						<span className="spacer" />
						<button className="btn btn--sm" onClick={csv}>Exportera CSV</button>
					</div>
					<div className="table-wrap">
						<table style={{minWidth: 0}}>
							<thead><tr>
								<th>Vecka</th>
								{data.columns.map(c => <th key={c._id}>{c.name}</th>)}
								<th>Händelser</th>
							</tr></thead>
							<tbody>
								{[...data.weeks].reverse().map(w => (
									<tr key={w.week}>
										<td>{w.week}</td>
										{data.columns.map(c => <td key={c._id}>{w.counts[c._id] ?? 0}</td>)}
										<td>{w.events}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className="panel__title">Senaste händelser</div>
					<ul className="assump">
						{data.activity.length === 0 && <li>Inget har hänt ännu.</li>}
						{data.activity.map((a, i) => <li key={i}>{when(a.at)} · <b>{a.title}</b> · {a.details} · {a.email}</li>)}
					</ul>
				</>
			)}
		</Drawer>
	);
}
